import type {
  CrAdVariant,
  CrDayBooking,
  CrFunnelStage,
  CrHeroMetric,
  CrKeywordRow,
  CrLogEntry,
  CrPatientValue,
  CrPeakHour,
} from './types';

export const CR_HERO_METRICS: CrHeroMetric[] = [
  { label: 'Ad Spend', value: '$620', sub: 'of $800 budget' },
  { label: 'Appointments Booked', value: '22', sub: '↑ 6 vs last month', positive: true, subPositive: true },
  { label: 'Cost Per Patient', value: '$28', sub: '↓ $7 vs last month', positive: true, subPositive: true },
  { label: 'Revenue Generated', value: '$8,400', sub: 'from booked appts', positive: true },
  { label: 'Return on Ad Spend', value: '13.5x', sub: '$1 spent = $13.50 back', positive: true },
  { label: 'Phone Calls', value: '38', sub: '58% → booked' },
];

export const CR_FUNNEL: CrFunnelStage[] = [
  { id: 'impressions', label: 'Impressions', value: 12400, note: '', width: 100, tone: 'neutral' },
  { id: 'clicks', label: 'Clicks', value: 485, note: '3.9% CTR', width: 70, tone: 'info' },
  { id: 'calls', label: 'Calls / Forms', value: 62, note: '12.8%', width: 50, tone: 'gold' },
  { id: 'booked', label: 'Appts Booked', value: 22, note: '35.5%', width: 35, tone: 'success' },
  { id: 'showed', label: 'Showed Up', value: 20, note: '90.9%', width: 30, tone: 'success', isLast: true },
];

export const CR_BOOKINGS_BY_DAY: CrDayBooking[] = [
  { day: 'Mon', value: 3 },
  { day: 'Tue', value: 2 },
  { day: 'Wed', value: 4 },
  { day: 'Thu', value: 3 },
  { day: 'Fri', value: 5 },
  { day: 'Sat', value: 4 },
  { day: 'Sun', value: 1 },
];

export const CR_PEAK_HOURS: CrPeakHour[] = [
  { slot: '8–10 AM', value: 6, note: 'Morning rush' },
  { slot: '10–12 PM', value: 4, note: '' },
  { slot: '12–2 PM', value: 3, note: 'Lunch break' },
  { slot: '2–4 PM', value: 2, note: '' },
  { slot: '4–6 PM', value: 5, note: 'After work' },
  { slot: '6–9 PM', value: 2, note: 'Evening' },
];

export const CR_PATIENT_VALUE: CrPatientValue[] = [
  { label: 'Avg First Visit Value', value: '$382', sub: 'exam + emergency treatment' },
  { label: 'Est. 12-Month LTV', value: '$1,450', sub: 'follow-ups + preventive' },
  { label: 'No-Show Rate', value: '9.1%', sub: '2 of 22 didn’t show' },
  { label: 'Lifetime ROI', value: '46x', sub: '$620 spend → $29K est. LTV' },
];

export const CR_KEYWORDS: CrKeywordRow[] = [
  { keyword: 'emergency dentist near me', match: 'Broad', impressions: 3200, clicks: 142, cpc: 1.35, bookings: 7, costPerBooking: 24.86, status: 'top' },
  { keyword: 'same day dental appointment', match: 'Phrase', impressions: 2100, clicks: 98, cpc: 1.12, bookings: 5, costPerBooking: 21.95, status: 'top' },
  { keyword: 'emergency tooth extraction', match: 'Exact', impressions: 1800, clicks: 72, cpc: 1.55, bookings: 4, costPerBooking: 27.9, status: 'good' },
  { keyword: 'broken tooth repair', match: 'Phrase', impressions: 1400, clicks: 58, cpc: 1.08, bookings: 3, costPerBooking: 20.88, status: 'good' },
  { keyword: 'dental pain relief', match: 'Broad', impressions: 1600, clicks: 54, cpc: 0.98, bookings: 2, costPerBooking: 26.46, status: 'good' },
  { keyword: '24 hour dentist', match: 'Phrase', impressions: 1200, clicks: 35, cpc: 1.42, bookings: 1, costPerBooking: 49.7, status: 'watch' },
  { keyword: 'tooth abscess treatment', match: 'Exact', impressions: 600, clicks: 18, cpc: 1.65, bookings: 0, costPerBooking: 0, status: 'paused' },
  { keyword: 'emergency root canal', match: 'Exact', impressions: 500, clicks: 8, cpc: 2.1, bookings: 0, costPerBooking: 0, status: 'paused' },
];

export const CR_AD_VARIANTS: CrAdVariant[] = [
  {
    label: 'Variant A',
    winner: true,
    headline: 'Emergency Dentist Open Now — Same-Day Appointments',
    description:
      "Severe tooth pain? Don't wait. Walk-ins welcome, most insurance accepted. Open late & weekends. Call now or book online instantly.",
    impressions: 6800,
    clicks: 268,
    ctr: 3.94,
    bookings: 14,
    costPerBooking: 24.29,
    share: '65%',
  },
  {
    label: 'Variant B',
    winner: false,
    headline: 'Same-Day Emergency Dental Care — Book in 60 Seconds',
    description:
      'Broken tooth, abscess, or dental trauma? See a dentist today. Gentle care, transparent pricing. Online booking available 24/7.',
    impressions: 4200,
    clicks: 158,
    ctr: 3.76,
    bookings: 6,
    costPerBooking: 33.17,
    share: '25%',
  },
  {
    label: 'Variant C',
    winner: false,
    headline: 'Tooth Pain Relief Today — No Wait Emergency Dentist',
    description:
      'Expert emergency dental care when you need it most. Sedation available. Most PPO accepted. Book your same-day visit now.',
    impressions: 1400,
    clicks: 59,
    ctr: 4.21,
    bookings: 2,
    costPerBooking: 38.5,
    share: '10%',
  },
];

export const CR_LOG: CrLogEntry[] = [
  {
    date: 'Apr 7',
    action: 'Increased daily budget by 10%',
    reason: 'CPC dropped 18% — opportunity to capture more volume at lower cost',
    impact: '+3 bookings in 2 days',
    type: 'budget',
  },
  {
    date: 'Apr 5',
    action: 'Paused keyword: "emergency root canal"',
    reason: '$16.80 spent, 0 conversions in 14 days — negative ROI',
    impact: 'Saved ~$4/day in wasted spend',
    type: 'keyword',
  },
  {
    date: 'Apr 3',
    action: 'Shifted 65% traffic to Variant A',
    reason:
      'Variant A: $24 cost/booking vs Variant B: $33 — statistically significant after 200 clicks each',
    impact: 'Avg cost/booking dropped $4',
    type: 'ad',
  },
  {
    date: 'Mar 30',
    action: 'Added Saturday/Sunday ad scheduling',
    reason: 'Detected 6 weekend calls with no ads running — competitor gap identified',
    impact: '4 weekend bookings in first week',
    type: 'schedule',
  },
  {
    date: 'Mar 26',
    action: 'Paused keyword: "tooth abscess treatment"',
    reason:
      'High CPC ($1.65), informational intent — users searching for home remedies, not appointments',
    impact: 'Redirected $30/week to better keywords',
    type: 'keyword',
  },
  {
    date: 'Mar 22',
    action: 'Created Variant C for testing',
    reason: 'Hypothesis: emphasizing sedation availability may improve CTR for anxious patients',
    impact: 'Testing — 4.21% CTR but lower booking rate',
    type: 'ad',
  },
  {
    date: 'Mar 18',
    action: 'Expanded geo radius by 2 miles',
    reason: '3 bookings came from just outside target area — search data shows demand',
    impact: '+2 bookings from expanded zone',
    type: 'geo',
  },
  {
    date: 'Mar 12',
    action: 'Campaign launched by ELVA',
    reason:
      'Market intelligence detected low CPC for emergency keywords + competitor pulled back spend',
    impact: '22 bookings in first month',
    type: 'launch',
  },
];
